import { Injectable, Signal, computed, signal } from '@angular/core';
import {
  IPrivacyAgreementIndication,
  IPrivacyAgreementIndications,
  IPrivacyData,
  IPrivacyModel
} from '@partners/api';
import { Observable } from 'rxjs';
import { StateAccessService } from '../../../services';
import { IPrivacyState, PrivacyActions, PrivacySelectors } from '../store';

/** fallback when the response carries no indication */
const NO_AGREEMENT_INDICATION: IPrivacyAgreementIndication = {
  isAvailable: false,
  isSelectable: false,
  privacyStatement: ''
};

@Injectable({
  providedIn: 'root'
})
export class PrivacyStore extends StateAccessService<IPrivacyState> {
  readonly state$: Observable<IPrivacyState>;

  readonly data$$: Signal<IPrivacyData>;
  readonly model$$: Signal<IPrivacyModel>;

  /** server-driven agreement indications (OMG-2833) */
  readonly operationalAgreement$$: Signal<IPrivacyAgreementIndication>;
  readonly marketingAgreement$$: Signal<IPrivacyAgreementIndication>;

  /**
   * captured once per load, NOT computed off data$$(): the PUT response replaces `data` wholesale,
   * which would restructure the screen mid-submit and bind fields the form never created.
   */
  private readonly _agreementIndications$$ = signal<IPrivacyAgreementIndications>(undefined);

  readonly agreementIndications$$: Signal<IPrivacyAgreementIndications> = this._agreementIndications$$;

  constructor() {
    super();
    this.model$$ = this.stateFacade.selectSignal$(PrivacySelectors.model);
    this.data$$ = this.stateFacade.selectSignal$(PrivacySelectors.data);

    this.operationalAgreement$$ = computed<IPrivacyAgreementIndication>(
      () => this._agreementIndications$$()?.operational ?? NO_AGREEMENT_INDICATION
    );

    this.marketingAgreement$$ = computed<IPrivacyAgreementIndication>(
      () => this._agreementIndications$$()?.marketing ?? NO_AGREEMENT_INDICATION
    );
  }

  /** undefined here correctly resets the screen to the pre-OMG-2833 layout */
  setAgreementIndications(indications: IPrivacyAgreementIndications) {
    this._agreementIndications$$.set(indications);
  }

  dispatchPrivacyReset() {
    this.stateFacade.dispatch(PrivacyActions.reset());
  }
  dispatchPrivacyLoad() {
    this.stateFacade.dispatch(PrivacyActions.load());
  }
  dispatchPrivacyLoadSuccess({ model, data }: { model: IPrivacyModel; data: IPrivacyData }) {
    this.stateFacade.dispatch(PrivacyActions.loadSuccess({ model, data }));
  }

  dispatchPrivacySubmit() {
    this.stateFacade.dispatch(PrivacyActions.submit());
  }
  dispatchPrivacySubmitSuccess({ model, data }: { model: IPrivacyModel; data: IPrivacyData }) {
    this.stateFacade.dispatch(PrivacyActions.submitSuccess({ model, data }));
  }
}
